import PropTypes from "prop-types";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import Slider from "components/shared/slider/Slider";
import { useMediaQueryContext } from "lib/mediaQueryContext";
import OptionWithValue from "../option/OptionWithValue";
import Product from "../product/Product";
import Skeleton from "../product/skeleton/Skeleton";

const SUPPORTED_PACKAGING = {
    STANDARD: 0,
    DELUXE: 1,
    BAG: 2
};

const AdditionalSale = forwardRef(
    ({ id, items, type, title, loading }, ref) => {
        const { desktopView, mobileView } = useMediaQueryContext();
        const name = id ? `${id}.${type}` : type;
        const checkedName = id ? `${id}.${type}.checked` : `${type}.checked`;
        const { watch, setValue } = useFormContext();
        const quantityName = id ? `${id}.quantity` : "quantity";
        const skuName = id ? `${id}.sku` : "sku";
        const formNode = watch(name);
        const watchFields = watch([quantityName, skuName]);
        const totalCards = Number(watch(quantityName));
        const totalStdPackaging = Number(
            watch(`${name}.items[${SUPPORTED_PACKAGING.STANDARD}].quantity`)
        );
        const totalLuxPackaging = Number(
            watch(`${name}.items[${SUPPORTED_PACKAGING.DELUXE}].quantity`)
        );
        const totalBagPackaging = Number(
            watch(`${name}.items[${SUPPORTED_PACKAGING.BAG}].quantity`)
        );
        const [active, setActive] = useState(false);

        const setPackageQuantity = (value, pkgType) => {
            setTimeout(
                () =>
                    setValue(`${name}.items[${pkgType}].quantity`, value, {
                        shouldDirty: true
                    }),
                0
            );
        };

        const checkPkgQuantities = pkgType => {
            if (
                type !== "packaging" ||
                Number.isNaN(totalStdPackaging) ||
                Number.isNaN(totalLuxPackaging)
            ) {
                return;
            }

            const totalPackaging = totalStdPackaging + totalLuxPackaging;

            if (totalPackaging <= totalCards) {
                return;
            }

            const pkgCurrentValue = watch(`${name}.items[${pkgType}].quantity`);
            const otherPackageQuantity = totalCards - pkgCurrentValue;
            const otherPackageType = (pkgType + 1) % 2;
            const otherPkgCurrentValue = watch(
                `${name}.items[${otherPackageType}].quantity`
            );

            // Only happens when pkgType is going beyond totalCards
            if (otherPackageQuantity < 0) {
                setPackageQuantity(totalCards, pkgType);
            }

            // Prevents a new render if the otherPkg is zero (because it can't go lower)
            if (otherPkgCurrentValue > 0) {
                setPackageQuantity(otherPackageQuantity, otherPackageType);
            }
        };

        // A state to check the initial checked state based on the products
        const checked = useMemo(() => {
            const HAS_QUANTITY = totalCards > 0;
            const HAS_PRODUCTS_WITH_QUANTITY =
                formNode?.items?.filter(item => Number(item.quantity) > 0)
                    ?.length > 0 ?? false;
            return HAS_QUANTITY && HAS_PRODUCTS_WITH_QUANTITY;
        }, [formNode?.items, totalCards]);

        useEffect(() => {
            setValue(checkedName, checked);
        }, [checked, checkedName, setValue]);

        // Syncing logic START
        useEffect(() => {
            // Only run for packagings
            if (type !== "packaging") {
                return;
            }

            const totalPackaging = totalStdPackaging + totalLuxPackaging;
            let difference = totalCards - totalPackaging;

            // If cards are added or removed, automatically add or remove gratis packaging
            if (difference > 0) {
                setPackageQuantity(
                    totalStdPackaging + difference,
                    SUPPORTED_PACKAGING.STANDARD
                );
            } else if (difference < 0) {
                if (totalStdPackaging) {
                    setPackageQuantity(
                        totalStdPackaging + difference,
                        SUPPORTED_PACKAGING.STANDARD
                    );
                    difference += totalStdPackaging;
                }

                if (difference < 0) {
                    setPackageQuantity(
                        totalLuxPackaging + difference,
                        SUPPORTED_PACKAGING.DELUXE
                    );
                }
            }

            // eslint-disable-next-line
        }, [totalCards]);

        useEffect(() => {
            checkPkgQuantities(SUPPORTED_PACKAGING.STANDARD);

            // eslint-disable-next-line
        }, [totalStdPackaging]);

        useEffect(() => {
            checkPkgQuantities(SUPPORTED_PACKAGING.DELUXE);

            // eslint-disable-next-line
        }, [totalLuxPackaging]);

        useEffect(() => {
            // Only run for packagings
            if (type !== "packaging") {
                return;
            }

            // Watches totalBagPackaging and sets it back to the totalCards amount if it surpasses it.
            if (totalBagPackaging > totalCards) {
                setPackageQuantity(SUPPORTED_PACKAGING.BAG, totalCards);
            }
            // eslint-disable-next-line
        }, [totalBagPackaging]);
        // Syncing logic END

        // This useEffect set checked 1 time if there are products with quantity

        let products = Array(3)
            .fill()
            .map((_, index) => <Skeleton key={index.toString()} />);
        let productsMobile = [...products];
        const isSlider = items?.length > 1;

        if (!loading) {
            products =
                items?.map((item, index) => (
                    <Product
                        key={item.id}
                        item={item}
                        index={index}
                        name={`${name}.items`}
                        slider={isSlider}
                    />
                )) ?? [];

            productsMobile =
                items?.map((item, index) => (
                    <Product
                        key={item.id}
                        item={item}
                        index={index}
                        name={`${name}.items`}
                        slider={isSlider}
                        mobile
                    />
                )) ?? [];
        }

        return (
            <OptionWithValue
                id={checkedName}
                active={active}
                setActive={setActive}
                title={title}
                disabled={
                    watchFields[quantityName] <= 0 ||
                    watchFields[skuName] === undefined
                }
                ref={ref}
                isSlider={isSlider}
            >
                {desktopView && products}
                {mobileView && isSlider && (
                    <Slider
                        items={productsMobile}
                        slideWidth="200px"
                        overflowVisible
                        noButtonsMobile
                    />
                )}
                {mobileView && !isSlider && productsMobile}
            </OptionWithValue>
        );
    }
);

AdditionalSale.displayName = "AdditionalSale";

AdditionalSale.defaultProps = {
    id: undefined,
    loading: false,
    items: undefined
};

AdditionalSale.propTypes = {
    id: PropTypes.string,
    loading: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
};

export default AdditionalSale;
